import React, { forwardRef, useRef, useState } from 'react'

import setaDropdownVerde from '../../../icones/icone-seta-dropdown-verde.png'
import Checkbox from '../../Mantine/Checkbox'

import * as S from './styles'
import { GlobalStyles } from './styles'

const MAX_SEARCH_LENGTH = 20

/**
 * @typedef {Object} SelectOption
 * @property {string} value
 * @property {string} label
 */

/**
 * @typedef {Object} MultiSelectProps
 * @property {React.ReactNode} [label]
 * @property {SelectOption[]} [options]
 * @property {(value: string[]) => void} [onChange]
 * @property {string | number} [width]
 * @property {string | number} [w]
 * @property {string[]} [value]
 * @property {boolean} [disabled]
 * @property {boolean} [searchable]
 * @property {number} [maxSelectedValues]
 */

/**
 * Componente de seleção múltipla customizado com suporte a busca, limite de seleção,
 * e visual com checkbox.
 *
 * @param {MultiSelectProps} props
 */
export default function MultiSelect({
  label = '',
  options = [],
  onChange,
  width,
  w,
  value,
  disabled,
  searchable = false,
  maxSelectedValues
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')

  const refSelect = useRef()

  const filterData = (query, selected, data) => {
    if (!query) return data
    return data.label.toLowerCase().includes(query.toLowerCase())
  }

  const handleCloseDropdown = () => {
    setIsOpen(false)
    setSearch('')
  }

  const onSearchChange = (val) => {
    if (val.length <= MAX_SEARCH_LENGTH) setSearch(val)
  }

  const handleOpenDropdown = (e) => {
    e.stopPropagation()
    const wrapper = refSelect.current?.closest('.mantine-MultiSelect-wrapper')
    const input = wrapper?.querySelector('input')
    if (input) {
      input.focus()
      input.dispatchEvent(
        new MouseEvent('mousedown', { bubbles: true, cancelable: true })
      )
    }
  }

  return (
    <>
      <GlobalStyles />
      <S.MultiSelect
        ref={refSelect}
        searchValue={search}
        onSearchChange={onSearchChange}
        searchable={searchable}
        width={width}
        w={w}
        onChange={onChange}
        disabled={disabled}
        radius="xl"
        zIndex={9999}
        nothingFound="Nenhum item encontrado"
        withinPortal
        disableSelectedItemFiltering
        maxSelectedValues={maxSelectedValues}
        value={value}
        filter={filterData}
        placeholder="Selecione"
        data={options}
        label={label}
        onDropdownOpen={() => setIsOpen(true)}
        onDropdownClose={handleCloseDropdown}
        rightSection={
          <S.Img
            isOpen={isOpen}
            alt="seta-dropdown"
            src={setaDropdownVerde}
            onClick={handleOpenDropdown}
          />
        }
        itemComponent={CustomItem}
        valueComponent={(props) => (
          <Value
            key={`${label}-${props.index}`}
            allSelected={value}
            options={options || []}
            isOpen={isOpen}
            searchable={searchable}
            {...props}
          />
        )}
      />
    </>
  )
}

const Value = ({ allSelected, isOpen, searchable, ...others }) => {
  if (others.index > 0 || (searchable && isOpen)) return null

  const moreThanOne = allSelected.length > 1

  const formattedLabel = (
    <S.Label key="more">
      {moreThanOne ? `${allSelected.length} Selecionados` : '1 Selecionado'}
    </S.Label>
  )

  return <S.LabelWrapper {...others}>{formattedLabel}</S.LabelWrapper>
}

const CustomItem = forwardRef(({ label, ...props }, ref) => {
  return (
    <S.CustomItemWrapper ref={ref} {...props}>
      <Checkbox checked={props.selected} readOnly />
      <S.CustomItemSpan>{label}</S.CustomItemSpan>
    </S.CustomItemWrapper>
  )
})

CustomItem.displayName = 'CustomItem'
